import { PromisedConfirmationModal } from 'app/shared/modal/confirmation/promised_confirmation_modal';
import { promisedDelay } from 'app/shared/utilities/promise';

type ModalCreator = (
    msg: string,
    confirmText: string,
    cancelText?: string,
    displayCancelButton?: boolean,
) => PromisedConfirmationModal;
type DelayedPromise = (ms: number) => Promise<void>;

export class AuthorizationErrorHandler {
    private _window: Window;
    private _promptOnLogout: boolean;
    private _promptAlreadyCancelled: boolean;
    private _isShowingPrompt: boolean;
    private _isShowingForbiddenPrompt: boolean;
    private readonly _createModal: ModalCreator;
    private readonly _delay: DelayedPromise;

    constructor(window: Window, createModal: ModalCreator, delay: DelayedPromise) {
        this._window = window;
        this._createModal = createModal;
        this._delay = delay;

        this._promptOnLogout = false;
        this._promptAlreadyCancelled = false;
        this._isShowingPrompt = false;
        this._isShowingForbiddenPrompt = false;
    }

    static createWithDefaults(window: Window): AuthorizationErrorHandler {
        const newModal = (msg, success, cancel, displayCancelButton) =>
            new PromisedConfirmationModal(msg, success, cancel, displayCancelButton);
        return new AuthorizationErrorHandler(window, newModal, promisedDelay);
    }

    onUnauthorizedRequest() {
        // don't show the warning if we're not yet logged in, if it's already showing or if we deliberately logged out
        if (!this._promptOnLogout || this._isShowingPrompt) {
            return Promise.resolve();
        }

        const msg = this._promptAlreadyCancelled
            ? `Your session is still expired, please login again.`
            : `Your session has expired, maybe someone else is using this account. Please login again.`;

        const modal = this._createModal(msg, 'Return to login screen', 'Cancel');

        this._isShowingPrompt = true;

        return modal.promise
            .then((status) => {
                this._isShowingPrompt = false;
                if (status === PromisedConfirmationModal.CONFIRMED) {
                    // yolo, cya everybody.. apparently this is how we return to the login screen
                    this._window.location.href = '/';
                } else {
                    this._promptAlreadyCancelled = true;
                    this._promptOnLogout = false;
                    // don't remind them for 10 more seconds, just in-case it fixes itself randomly.
                    return this._delay(10000).finally(() => {
                        // okay, if it happens again, you'll know about it
                        this._promptOnLogout = true;
                    });
                }
            })
            .catch((error) => {
                this._isShowingPrompt = false;
                // not sure how this can happen, just log it
                console.error(error);
            });
    }

    onForbiddenRequest() {
        const msg =
            'Login succeeded with given credentials but user account is not a member of any IMS groups; cannot continue.';

        const modal = this._createModal(msg, 'Logout', '', false);

        this._isShowingForbiddenPrompt = true;

        return modal.promise
            .then((_) => {
                this._isShowingForbiddenPrompt = false;
                this._window.location.href = '/';
            })
            .catch((error) => {
                this._isShowingForbiddenPrompt = false;
                console.error(error);
            });
    }
}
